import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth';
import { useEffect } from 'react';
import { BrandedSpinner } from './components/ui/BrandedSpinner';

// Design system v1.0.0 - deployed 2026-01-10

// Layouts
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';

// Auth Pages
import { LoginPage } from './pages/auth/LoginPage';
import { RegisterPage } from './pages/auth/RegisterPage';
import { CheckEmailPage } from './pages/auth/CheckEmailPage';
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage';
import { ExtensionAuthPage } from './pages/auth/ExtensionAuthPage';
import { ExtensionSsoPage } from './pages/auth/ExtensionSsoPage';
import { GoogleCallbackPage } from './pages/auth/GoogleCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { OrganizationsPage } from './pages/orgs/OrganizationsPage';
import { OrganizationDetailPage } from './pages/orgs/OrganizationDetailPage';
import { ProjectDetailPage } from './pages/projects/ProjectDetailPage';
import { EpicsPage } from './pages/epics/EpicsPage';
import { EpicDetailPage } from './pages/epics/EpicDetailPage';
import { StoryDetailPage } from './pages/stories/StoryDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { TracesPage } from './pages/telemetry/TracesPage';
import { TraceDetailPage } from './pages/telemetry/TraceDetailPage';
import { KnowledgeSourcesPage } from './pages/projects/KnowledgeSourcesPage';
import { DomainRestrictionsPage } from './pages/projects/DomainRestrictionsPage';
import { WhiteboardPage } from './pages/projects/WhiteboardPage';
import { WhiteboardDemoPage } from './pages/WhiteboardDemoPage';
import { ProjectSettingsPage } from './pages/projects/ProjectSettingsPage';
import { ProjectAnalyticsPage } from './pages/projects/ProjectAnalyticsPage';
import { StoryMapPage } from './pages/projects/StoryMapPage';

// Initialize theme on app load
function useThemeInit() {
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);
}

// Listen for extension auth notifications (SSO sync)
function useExtensionSync() {
  const { logout, checkExtension, setTokenAndFetchUser, isAuthenticated } = useAuthStore();
  
  useEffect(() => {
    // Check if extension is installed on app load
    checkExtension().catch(console.warn);
    
    // Listen for auth notifications from extension
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'SPECUNO_EXTENSION_LOGOUT') {
        console.log('[App] Received logout notification from extension');
        // Pass false to skip syncing back to extension (prevents infinite loop)
        logout(false);
      } else if (event.data?.type === 'SPECUNO_EXTENSION_LOGIN') {
        // Prevent infinite reload loop
        const newToken = event.data.token;
        
        if (!newToken) return;
        
        // Skip if we just processed this token (prevent reload loop)
        const lastProcessedToken = sessionStorage.getItem('specuno_last_sso_token');
        if (lastProcessedToken === newToken) {
          console.log('[App] Skipping duplicate SSO notification');
          return;
        }
        
        console.log('[App] Received login notification from extension:', event.data.user?.email);
        
        try {
          // Mark this token as processed
          sessionStorage.setItem('specuno_last_sso_token', newToken);
          
          // Clear old state first to avoid confusion
          localStorage.removeItem('ba-auth');
          await setTokenAndFetchUser(newToken);
          console.log('[App] Successfully synced auth from extension');
          
          // Navigate to dashboard instead of reloading (prevents infinite loop)
          if (window.location.pathname.includes('/login') || window.location.pathname.includes('/register')) {
            window.location.href = '/app';
          }
        } catch (error) {
          console.error('[App] Failed to sync auth from extension:', error);
          // Clear the processed token so retry is possible
          sessionStorage.removeItem('specuno_last_sso_token');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [logout, checkExtension, setTokenAndFetchUser, isAuthenticated]);
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-main">
        <BrandedSpinner size="lg" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Component to handle root redirect based on auth state
function RootRedirect() {
  const { isAuthenticated, isLoading } = useAuthStore();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-main">
        <BrandedSpinner size="lg" />
      </div>
    );
  }
  
  return <Navigate to={isAuthenticated ? '/app' : '/login'} replace />;
}

export default function App() {
  useThemeInit();
  useExtensionSync();
  
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />
      
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/check-email" element={<CheckEmailPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>
      
      {/* Extension auth - standalone page */}
      <Route path="/auth/extension" element={<ExtensionAuthPage />} />
      <Route path="/auth/extension/sso" element={<ExtensionSsoPage />} />
      
      {/* Google OAuth callback */}
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      
      {/* Public Whiteboard Demo (no auth required) */}
      <Route path="/whiteboard" element={<WhiteboardDemoPage />} />
      
      {/* Protected dashboard routes under /app */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        
        {/* Organizations */}
        <Route path="organizations" element={<OrganizationsPage />} />
        <Route path="organizations/:orgId" element={<OrganizationDetailPage />} />
        
        {/* Projects (nested under org) */}
        <Route path="organizations/:orgId/projects/:projectId" element={<ProjectDetailPage />} />
        
        {/* Epics (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/epics" element={<EpicsPage />} />
        <Route path="organizations/:orgId/projects/:projectId/epics/:epicId" element={<EpicDetailPage />} />
        
        {/* Stories (nested under epic) */}
        <Route path="organizations/:orgId/projects/:projectId/epics/:epicId/stories/:storyId" element={<StoryDetailPage />} />
        
        {/* Telemetry (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/telemetry" element={<TracesPage />} />
        <Route path="organizations/:orgId/projects/:projectId/telemetry/:traceId" element={<TraceDetailPage />} />
        
        {/* Knowledge Base (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/knowledge" element={<KnowledgeSourcesPage />} />
        
        {/* Domain Restrictions (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/domains" element={<DomainRestrictionsPage />} />
        
        {/* Whiteboard (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/whiteboard" element={<WhiteboardPage />} />
        
        {/* Project Settings (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/settings" element={<ProjectSettingsPage />} />
        
        {/* Project Analytics (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/analytics" element={<ProjectAnalyticsPage />} />
        
        {/* Story Map (nested under project) */}
        <Route path="organizations/:orgId/projects/:projectId/story-map" element={<StoryMapPage />} />
        
        {/* Settings */}
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      
      {/* Fallback - redirect to root which will handle auth check */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
